import { useState, useEffect, useCallback } from 'react';

/**
 * Generic async data fetching hook.
 * @param {Function} fetchFn - async function that returns data
 * @param {Array} deps - dependency array that triggers re-fetch
 */
export const useFetch = (fetchFn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result.data?.data ?? result.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro desconhecido.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, refetch: load };
};

/**
 * Generic async mutation hook (create/update/delete).
 */
export const useMutation = (mutateFn) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await mutateFn(...args);
      return result.data?.data ?? result.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erro ao executar operação.';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [mutateFn]);

  return { mutate, loading, error };
};
